import { yupResolver } from '@hookform/resolvers/yup';
import { endOfDay, isBefore, startOfToday } from 'date-fns';
import { memo, useCallback, useEffect, useId } from 'react';
import { useForm } from 'react-hook-form';
import { useToggle } from 'react-use';
import { date, type InferType, object } from 'yup';

import { useTrackAddedShareLinkExpirationDate } from '@air/analytics';
import { type ShortUrlResponse } from '@air/api/types';
import { Button } from '@air/primitive-button';
import { DateInput } from '@air/primitive-date-input';
import { parseAirDateToISO } from '@air/utilities';
import { cn } from '@air/utils-tailwind';

import { OnOffShareToggleRow } from '~/components/ShareModal/components/OnOffShareToggleRow';
import { UpsellFlashIconButton } from '~/components/ShareModal/components/UpsellFlashIconButton';
import { SHARE_MODAL_UPSELL_MESSAGE } from '~/constants/upsells';
import { useShareSettings } from '~/hooks/useShareSettings';

export const ExpirationDateRowFormValidationSchema = object({
  expirationDate: date()
    .nullable()
    .test(
      'not-past',
      'Expiration date cannot be set in the past',
      (value) => !value || !isBefore(value, startOfToday()),
    )
    .default(null),
});

export type ExpirationDateRowFormInput = InferType<typeof ExpirationDateRowFormValidationSchema>;

export type ExpirationDateRowProps = {
  shortIdInfo: ShortUrlResponse;
  onChangeExpirationDate: (expirationDate: string | null) => Promise<unknown>;
  settingsDisabled?: boolean;
  /**
   * Hide the per-row Save button and instead commit every change to the
   * parent on the fly. Use this when the parent owns a footer CTA that
   * persists everything at once (e.g. the create-collection flow).
   */
  hideSaveButton?: boolean;
};

export const ExpirationDateRow = memo(
  ({
    shortIdInfo,
    onChangeExpirationDate,
    hideSaveButton = false,
    settingsDisabled = false,
  }: ExpirationDateRowProps) => {
    const formId = useId();
    const { expirationDateEnabled } = useShareSettings();
    const initialExpirationDate = shortIdInfo.expirationDate ? parseAirDateToISO(shortIdInfo.expirationDate) : null;
    const [isShowingExpirationDateForm, showExpirationDateForm] = useToggle(!!initialExpirationDate);
    const { trackAddedShareLinkExpirationDate } = useTrackAddedShareLinkExpirationDate();

    const {
      formState: { errors, isDirty, isSubmitting, isValid },
      handleSubmit,
      reset,
      setValue,
      watch,
    } = useForm<ExpirationDateRowFormInput>({
      defaultValues: { expirationDate: initialExpirationDate },
      mode: 'all',
      resolver: yupResolver(ExpirationDateRowFormValidationSchema),
    });

    const watchedDate = watch('expirationDate');

    const handleDateChange = useCallback(
      (date: Date | [Date, Date] | null) => {
        if (Array.isArray(date)) {
          return;
        }
        setValue('expirationDate', date ? endOfDay(date) : null, { shouldDirty: true, shouldValidate: true });
      },
      [setValue],
    );

    const handleToggleChange = useCallback(
      async (checked: boolean) => {
        if (!checked) {
          if (shortIdInfo.expirationDate && isShowingExpirationDateForm) {
            try {
              await onChangeExpirationDate(null);
            } catch {
              // Server-side clear failed — leave the toggle on so the user
              // can see the expiration is still in effect and retry.
              return;
            }
          }
          reset({ expirationDate: null });
        }
        showExpirationDateForm(checked);
      },
      [shortIdInfo.expirationDate, isShowingExpirationDateForm, onChangeExpirationDate, showExpirationDateForm, reset],
    );

    const onSubmit = useCallback(
      async ({ expirationDate }: ExpirationDateRowFormInput) => {
        if (!expirationDate) {
          return;
        }
        try {
          // Skip tracking when there's no real shortId — the create flow
          // synthesizes an empty `id` and re-fires this event with the real
          // id after `handleCreate`, so tracking here would duplicate the
          // event with empty data.
          if (shortIdInfo.id) {
            trackAddedShareLinkExpirationDate({ shortId: shortIdInfo.id });
          }
          await onChangeExpirationDate(expirationDate.toISOString());
          reset({ expirationDate });
        } catch {
          // Swallow: the mutation surfaces its own error UI; we just keep the
          // form in its dirty state so the user can retry.
        }
      },
      [trackAddedShareLinkExpirationDate, shortIdInfo.id, onChangeExpirationDate, reset],
    );

    // Auto-commit mode: when the Save button is hidden, push every dirty
    // change up to the parent so it can include the latest value in its
    // own (footer) submit. Gated on `isDirty` so the initial mount doesn't
    // re-commit the seeded value, and on `isValid` to mirror the Save
    // button's validation check — only valid values are committed.
    useEffect(() => {
      if (!hideSaveButton || !isDirty || !isValid) {
        return;
      }
      void onChangeExpirationDate(watchedDate ? watchedDate.toISOString() : null);
    }, [hideSaveButton, isDirty, isValid, watchedDate, onChangeExpirationDate]);

    const expirationDateError = errors.expirationDate?.message;
    const showError = isShowingExpirationDateForm && expirationDateEnabled && !!expirationDateError;

    return (
      <div className="flex flex-col gap-2">
        <OnOffShareToggleRow
          label="Add an expiration date"
          labelAccessory={
            !expirationDateEnabled && <UpsellFlashIconButton>{SHARE_MODAL_UPSELL_MESSAGE}</UpsellFlashIconButton>
          }
          checked={isShowingExpirationDateForm}
          disabled={settingsDisabled || !expirationDateEnabled}
          isLoading={false}
          onToggle={handleToggleChange}
          data-testid="EXPIRATION_DATE_LABEL"
        />

        {expirationDateEnabled && !settingsDisabled && isShowingExpirationDateForm && (
          <div>
            <form id={formId} onSubmit={handleSubmit(onSubmit)}>
              <div className="flex items-center gap-2">
                <DateInput
                  size="medium"
                  testId="EXPIRATION_DATE_INPUT"
                  className={cn('w-full rounded-8', showError && 'ads:border-error border-red-9')}
                  name={`${formId}-expirationDate`}
                  placeholderText="MM/DD/YYYY"
                  selected={watchedDate ?? undefined}
                  onChange={handleDateChange}
                />
                {!hideSaveButton && (
                  <Button
                    appearance="filled"
                    color="blue"
                    data-testid="EXPIRATION_DATE_SUBMIT_BUTTON"
                    disabled={!isDirty || !isValid || isSubmitting}
                    isLoading={isSubmitting}
                    size="medium"
                    type="submit"
                  >
                    Save
                  </Button>
                )}
              </div>
            </form>
            {showError && (
              <p className="mb-0 ads:text-error text-12 text-red-9" data-testid="EXPIRATION_DATE_INPUT-error">
                {expirationDateError}
              </p>
            )}
          </div>
        )}
      </div>
    );
  },
);

ExpirationDateRow.displayName = 'ExpirationDateRow';
